import React from "react";
import { Handle, Position } from "@xyflow/react";
import { CONNECTOR_LABEL, SIGNAL_TYPE_LABEL as SIGNAL_LABEL } from "../constants/signalLabels";

const POSITION_MAP = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

const SIGNAL_TYPE_CLASS = {
  audio: "port-audio",
  midi: "port-midi",
  control: "port-control",
  usb_audio: "port-audio",
  power: "port-power",
};

const SIGNAL_TYPE_LABEL = {
  audio: "Audio port",
  midi: "MIDI port",
  control: "Control port",
  usb_audio: "Audio port",
  power: "Power port",
};

const CONNECTOR_CLASS = {
  quarter_inch_ts: "port-connector-quarter-inch",
  quarter_inch_trs: "port-connector-quarter-inch",
  eighth_inch_trs: "port-connector-eighth-inch",
  eighth_inch_trrs: "port-connector-eighth-inch",
  xlr: "port-connector-xlr",
  xlr_combo: "port-connector-xlr-combo",
  speakon: "port-connector-speakon",
  midi_din: "port-connector-midi-din",
  usb_a: "port-connector-usb",
  usb_b: "port-connector-usb",
  usb_c: "port-connector-usb",
  barrel_2_1mm: "port-connector-barrel",
  barrel_2_5mm: "port-connector-barrel",
  proprietary: "port-connector-proprietary",
  mini_xlr_3pin: "port-connector-mini-xlr",
  mini_xlr_4pin: "port-connector-mini-xlr",
  ultranet: "port-connector-proprietary",
  wireless: "port-connector-wireless",
};

const POWER_CONNECTOR_CLASS = {
  barrel_2_1mm: "port-power-barrel-sm",
  barrel_2_5mm: "port-power-barrel-lg",
  rca: "port-power-rca",
  ac_cable: "port-power-ac",
  usb_a: "port-power-usb",
  usb_c: "port-power-usb",
};

const POLARITY_SYMBOL = {
  center_negative: "\u2212",
  center_positive: "+",
};

const SIGNAL_LEVEL_CLASS = {
  instrument: "port-level-instrument",
  mic: "port-level-mic",
  line: "port-level-line",
  speaker: "port-level-speaker",
  mic_line: "port-level-mic-line",
};


export function portHandleClass({ signal_type, connector, signal_level }) {
  const signalType = signal_type || "audio";
  const isPower = signalType === "power";

  if (isPower) {
    const powerConnClass = POWER_CONNECTOR_CLASS[connector] || "port-power-other";
    return `port-handle port-power ${powerConnClass}`;
  }

  const connectorClass = CONNECTOR_CLASS[connector] || "";
  const isAudio = signalType === "audio" || signalType === "usb_audio";
  const levelClass = isAudio
    ? (SIGNAL_LEVEL_CLASS[signal_level] || "port-level-instrument")
    : "";
  return `port-handle ${SIGNAL_TYPE_CLASS[signalType] || "port-audio"} ${connectorClass} ${levelClass}`.trim();
}

export default function PortHandle({ port, position, excluded = false }) {
  const handleType = port.direction === "input" ? "target" : "source";
  const signalType = port.signal_type || "audio";
  const isPower = signalType === "power";
  const connectorLabel = CONNECTOR_LABEL[port.connector] || port.connector;

  const className = `${portHandleClass(port)}${excluded ? " port-excluded" : ""}`;

  const polaritySymbol = isPower ? POLARITY_SYMBOL[port.polarity] : null;

  let tooltip;
  if (port.powerTooltip) {
    tooltip = `${port.name} — ${port.powerTooltip}`;
  } else if (port.signal_types && port.signal_types.length > 1) {
    const typeLabels = port.signal_types.map((t) => SIGNAL_LABEL[t] || t).join(" + ");
    tooltip = `${port.name} (${connectorLabel} — ${typeLabels})`;
  } else {
    tooltip = `${port.name} (${connectorLabel})`;
  }

  return (
    <Handle
      type={handleType}
      position={POSITION_MAP[position] || Position.Left}
      id={String(port.id)}
      className={className}
      title={tooltip}
      role="button"
      aria-label={SIGNAL_TYPE_LABEL[signalType] || "Audio port"}
    >
      {polaritySymbol && (
        <span className={`port-power-polarity port-power-polarity--${port.polarity === "center_positive" ? "positive" : "negative"}`} aria-hidden="true">
          {polaritySymbol}
        </span>
      )}
    </Handle>
  );
}
